import type { BaseSearchResultProps } from 'blocks/shared/search-results/search-results.ts';
import { BaseSearchResults } from 'blocks/shared/search-results/search-results.ts';
import {
	button,
	output,
	table,
	tbody,
	td,
	th,
	thead,
	tr,
} from 'scripts/dom-helpers.js';
import { getDateStringFromExcelSerialNumber } from 'utils/dateUtils.ts';
import showDownloadsModal from './downloads-modal.ts';
import type { AgreementCheckboxOptions } from './downloads.types.ts';
import type { GroupedDownload } from './downloads.utils.ts';

export type DownloadSortOptions = {
	column?: 'date' | 'version';
	direction?: 'asc' | 'desc';
};

export type DownloadSearchResultProps =
	BaseSearchResultProps<GroupedDownload> & {
		onSort: (sortOptions: DownloadSortOptions) => void;
		linkPrefix?: string;
	} & AgreementCheckboxOptions;

export class DownloadsSearchResults extends BaseSearchResults<GroupedDownload> {
	constructor({
		linkPrefix,
		onSort,
		disableSoftwareLicense,
		disableTermsPrivacy,
		...props
	}: DownloadSearchResultProps) {
		super({
			...props,
			searchResultResultsElement: output({ class: 'downloads__results' }),
		});
		this.searchResultParentElement = tbody();
		this.versionHeader = this.buildVersionHeader();
		this.dateHeader = this.buildDateHeader();
		const searchTable = table(
			this.buildTableHeader(),
			this.searchResultParentElement,
		);
		this.searchResultsElement.appendChild(searchTable);
		this.renderResultFn = this.renderResult;
		this.renderNoResultFn = this.renderNoResults;
		this.linkPrefix = linkPrefix;
		this.onSortFn = onSort;
		this.disableSoftwareLicense = disableSoftwareLicense;
		this.disableTermsPrivacy = disableTermsPrivacy;
	}

	onSortFn: (sortOptions: DownloadSortOptions) => void;
	linkPrefix?: string;
	disableSoftwareLicense?: boolean;
	disableTermsPrivacy?: boolean;
	versionHeader: HTMLTableCellElement;
	dateHeader: HTMLTableCellElement;

	sortAndRender = (column: 'date' | 'version') => {
		const sortColumn = column === 'date' ? this.dateHeader : this.versionHeader;
		const currentSort = sortColumn.getAttribute('data-sort');
		const direction = currentSort
			? currentSort === 'asc'
				? 'desc'
				: 'asc'
			: 'desc';

		this.element.querySelectorAll('th[data-sort]').forEach((el) => {
			el.removeAttribute('data-sort');
		});
		sortColumn.setAttribute('data-sort', direction);

		// Update aria-label to indicate sort direction
		const sortedText =
			direction === 'asc'
				? this.placeholders.downloadSortedAscending || 'sorted ascending'
				: this.placeholders.downloadSortedDescending || 'sorted descending';
		sortColumn
			.querySelector('button')
			?.setAttribute(
				'aria-label',
				`${column === 'date' ? this.placeholders.downloadsDate || 'Date' : this.placeholders.downloadsVersion || 'Version'}, ${sortedText}`,
			);

		// Remove aria-label from the other column
		(column === 'date' ? this.versionHeader : this.dateHeader)
			.querySelector('button')
			?.removeAttribute('aria-label');

		this.onSortFn({ column, direction });
	};

	renderResult = (download: GroupedDownload) => {
		const date = download.Date
			? getDateStringFromExcelSerialNumber(download.Date)
			: '';

		const launchModal = async () =>
			await showDownloadsModal(
				download.downloads,
				this.placeholders,
				{
					disableSoftwareLicense: this.disableSoftwareLicense,
					disableTermsPrivacy: this.disableTermsPrivacy,
				},
				this.linkPrefix,
			);
		const productCell: HTMLTableCellElement = td(
			button(
				{
					class: 'downloads__product-button',
					onclick: launchModal,
					type: 'button',
				},
				download.Title,
			),
		);
		const versionCell = td(
			button(
				{
					class: 'downloads__version-button',
					onclick: launchModal,
					type: 'button',
					ariaLabel: download.Version + ' ' + download.Title,
				},
				download.Version,
			),
		);
		return tr(
			productCell,
			td(download.Product || ''),
			td(download.Type || ''),
			td(download.downloads.map((d) => d.Language).join(', ') || ''),
			td(date),
			versionCell,
		);
	};

	renderNoResults = () => {
		this.searchResultParentElement?.append(
			tr(
				td(
					{ colspan: 5 },
					this.placeholders.searchNoResults || 'No results found.',
				),
			),
		);
	};

	buildVersionHeader = () => {
		const versionButton = button(
			{ class: 'downloads__job-version-button' },
			this.placeholders.downloadsVersion || 'Version',
		);
		versionButton.onclick = () => this.sortAndRender('version');
		return th({ class: 'downloads__job-version' }, versionButton);
	};

	buildDateHeader = () => {
		const dateButton = button(
			{ class: 'downloads__date-button' },
			this.placeholders.downloadsDate || 'Date',
		);
		dateButton.onclick = () => this.sortAndRender('date');
		return th({ class: 'downloads__date' }, dateButton);
	};

	buildTableHeader = () => {
		const tableHead = thead(
			tr(
				th(
					{ class: 'downloads__title' },
					this.placeholders.downloadsTitle || 'Title',
				),
				th(
					{ class: 'downloads__product' },
					this.placeholders.downloadsProduct || 'Product',
				),
				th(
					{ class: 'downloads__type' },
					this.placeholders.downloadsType || 'Type',
				),
				th(
					{ class: 'downloads__language' },
					this.placeholders.downloadsLanguage || 'Language',
				),
				this.dateHeader,
				this.versionHeader,
			),
		);
		return tableHead;
	};
}
